/**
 * PropertyService - Central service for managing reactive properties
 *
 * Features:
 * - Creates and manages ReactiveProperty instances
 * - Handles automatic database persistence (debounced)
 * - Works in both React components and Potree code
 * - Single source of truth for all property values
 */

import { ReactiveProperty, Subscription } from './ReactiveProperty';
import { PropertyType, PropertyValueData } from '../types';
import { propertyRegistry } from '../PropertyRegistry';

export interface PropertyKey {
  identifier: string;
  objectIdentifier: string;
  propertyType: PropertyType;
}

export interface PropertyUpdate {
  identifier: string;
  objectIdentifier: string;
  propertyType: PropertyType;
  value: PropertyValueData;
}

export interface PropertyServiceConfig {
  projectId: string;
  /**
   * Callback to persist property to database
   * Return true if save was successful
   */
  onSave?: (identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData) => Promise<boolean>;
  /**
   * Callback to persist multiple properties to database in batch
   * Return true if save was successful
   */
  onSaveBatch?: (updates: PropertyUpdate[]) => Promise<boolean>;
  /**
   * Callback to load property from database
   * Can return value synchronously or as a Promise
   */
  onLoad?: (identifier: string, objectIdentifier: string, propertyType: PropertyType) => PropertyValueData | undefined | Promise<PropertyValueData | undefined>;
  /**
   * Callback triggered immediately on every property change (no debounce)
   * Use this to push real-time updates via websockets while database save is debounced
   */
  onImmediateChange?: (identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData) => void;
  /**
   * Callback triggered once after a batch of property changes (via batchUpdate).
   * Receives all updates at once, so the caller can send a single Pusher event.
   * If not provided, onImmediateChange is called per-property as fallback.
   */
  onImmediateChangeBatch?: (updates: PropertyUpdate[]) => void;
  /**
   * Debounce time in ms before saving to database (default: 500ms)
   */
  debounceMs?: number;
  /**
   * Synchronous callback for flushing pending saves during page unload.
   * Receives all pending property updates. Use navigator.sendBeacon for reliable delivery.
   * Called from the 'beforeunload' event where async operations are not guaranteed to complete.
   */
  onBeforeUnloadFlush?: (updates: PropertyUpdate[]) => void;
}

/**
 * Central service for managing reactive properties across the application
 */
/** Shape of a property group as returned by the GraphQL property queries */
export interface PreloadPropertyGroup {
  propertyIdentifier: string;
  properties: Array<{ identifier: string; value: PropertyValueData }>;
}

export class PropertyService {
  private properties = new Map<string, ReactiveProperty<PropertyValueData>>();
  private saveTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private config: PropertyServiceConfig;
  /** Keys of properties currently being updated from remote - skip broadcasting for these */
  private remoteUpdateInProgress = new Set<string>();
  /** Keys of properties with pending local changes - skip remote updates for these */
  private pendingLocalChanges = new Set<string>();
  /** When true, skip broadcasts and debounced saves (used during mutation-backed creation) */
  private localOnlyMode = false;
  /** When true, skip individual broadcasts (batchUpdate sends one batch broadcast at the end) */
  private batchInProgress = false;
  /** Pre-loaded values from bulk GraphQL fetch, keyed by "identifier:objectIdentifier:propertyType" */
  private preloadedValues = new Map<string, PropertyValueData>();

  constructor(config: PropertyServiceConfig) {
    this.config = {
      debounceMs: 500,
      ...config,
    };
  }

  /**
   * Generate a unique key for a property
   */
  private createKey(identifier: string, objectIdentifier: string, propertyType: PropertyType): string {
    return `${identifier}:${objectIdentifier}:${propertyType}`;
  }

  /**
   * Bulk-load property values from GraphQL query results into an internal lookup table.
   * Call this once when the property query completes. Subsequent getProperty() calls
   * will read initial values from this table in O(1) instead of calling onLoad per property.
   *
   * @param groups - Property groups from the GraphQL response
   * @param propertyType - Which property type these groups belong to
   */
  preloadValues(groups: PreloadPropertyGroup[], propertyType: PropertyType): void {
    for (const group of groups) {
      for (const prop of group.properties) {
        const key = this.createKey(prop.identifier, group.propertyIdentifier, propertyType);
        this.preloadedValues.set(key, prop.value);

        // If a ReactiveProperty already exists for this key and still has its default value,
        // update it with the preloaded value (handles race where property was created before data arrived)
        const existing = this.properties.get(key);
        if (existing) {
          const config = propertyRegistry.getConfig(prop.identifier);
          if (config) {
            const staticDefault = config.defaultValue;
            const dynamicDefault = config.getDefaultValue?.();
            const isDefault = existing.value === staticDefault || (dynamicDefault !== undefined && existing.value === dynamicDefault);
            if (isDefault && prop.value !== staticDefault) {
              this.setValueFromRemote(prop.identifier, group.propertyIdentifier, propertyType, prop.value);
            }
          }
        }
      }
    }
  }

  /**
   * Get or create a reactive property instance
   * This is the main method to access properties from anywhere (React or Potree)
   */
  getProperty(identifier: string, objectIdentifier: string, propertyType: PropertyType): ReactiveProperty<PropertyValueData> {
    const key = this.createKey(identifier, objectIdentifier, propertyType);

    // Return existing property if it exists
    const existing = this.properties.get(key);
    if (existing) {
      return existing;
    }

    // Create new property
    const config = propertyRegistry.getConfig(identifier);
    if (!config) {
      throw new Error(`Property "${identifier}" is not registered. Register it first using propertyRegistry.register()`);
    }

    // Resolve initial value: preloaded bulk data > onLoad callback > dynamic default > static default
    let initialValue = config.getDefaultValue ? config.getDefaultValue() : config.defaultValue;
    const preloaded = this.preloadedValues.get(key);
    if (preloaded !== undefined) {
      initialValue = preloaded;
    } else if (this.config.onLoad) {
      try {
        const loadedValue = this.config.onLoad(identifier, objectIdentifier, propertyType);
        if (loadedValue !== undefined && !(loadedValue instanceof Promise)) {
          initialValue = loadedValue;
        } else if (loadedValue instanceof Promise) {
          loadedValue
            .then((value) => {
              if (value !== undefined) {
                this.setValueFromRemote(identifier, objectIdentifier, propertyType, value);
              }
            })
            .catch((error) => {
              console.error(`Error loading property ${identifier}:`, error);
            });
        }
      } catch (error) {
        console.error(`Error loading property ${identifier}:`, error);
      }
    }

    // Create reactive property with initial value (either default or loaded)
    const reactiveProperty = new ReactiveProperty<PropertyValueData>(initialValue);

    // Set up auto-save on changes (debounced).
    // Done synchronously — subscribeToChanges only fires on future changes, not the current value.
    // Previous versions used queueMicrotask to avoid saving the initial value, but with preloadValues
    // the initial value is already correct so no spurious save triggers.
    reactiveProperty.subscribeToChanges((value) => {
      this.scheduleSave(identifier, objectIdentifier, propertyType, value);
    });

    // Store the property
    this.properties.set(key, reactiveProperty);

    return reactiveProperty;
  }

  /**
   * Check if a property instance exists
   */
  hasProperty(identifier: string, objectIdentifier: string, propertyType: PropertyType): boolean {
    const key = this.createKey(identifier, objectIdentifier, propertyType);
    return this.properties.has(key);
  }

  /**
   * Whether a non-default value exists for this property — either preloaded from
   * the bulk GraphQL fetch or explicitly set on a live instance. Unlike
   * `hasProperty`, a lazily-created instance still holding its registry default
   * does NOT count, so callers can distinguish "never set" from "set to default".
   */
  hasStoredValue(identifier: string, objectIdentifier: string, propertyType: PropertyType): boolean {
    const key = this.createKey(identifier, objectIdentifier, propertyType);
    if (this.preloadedValues.has(key)) return true;
    const existing = this.properties.get(key);
    if (!existing) return false;
    const config = propertyRegistry.getConfig(identifier);
    return config ? existing.value !== config.defaultValue : true;
  }

  /**
   * Get property value synchronously (returns default if not loaded)
   */
  getValue(identifier: string, objectIdentifier: string, propertyType: PropertyType): PropertyValueData {
    const property = this.getProperty(identifier, objectIdentifier, propertyType);
    return property.value;
  }

  /**
   * Set multiple property values locally without triggering broadcasts or debounced saves.
   * Local subscribers ARE notified (for rendering), but no network requests are made.
   * Use this during annotation creation where the GraphQL mutation already persists the data.
   */
  setValuesLocally(updates: Array<{ identifier: string; objectIdentifier: string; propertyType: PropertyType; value: PropertyValueData }>): void {
    this.localOnlyMode = true;
    try {
      for (const update of updates) {
        const property = this.getProperty(update.identifier, update.objectIdentifier, update.propertyType);
        if (property.value !== update.value) {
          property.next(update.value);
        }
      }
    } finally {
      this.localOnlyMode = false;
    }
  }

  /**
   * Set property value (triggers save to database)
   * Uses next() to ensure immediate notification to all subscribers
   */
  setValue(identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData): void {
    const property = this.getProperty(identifier, objectIdentifier, propertyType);
    // Use next() to force immediate notification if value changed
    if (property.value !== value) {
      property.next(value);
    }
  }

  /**
   * Set property value from a remote update (does NOT trigger save to database or broadcast)
   * Use this when receiving updates via websockets to avoid saving back to the server
   */
  setValueFromRemote(identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData): void {
    const key = this.createKey(identifier, objectIdentifier, propertyType);

    // Check if property exists
    if (!this.properties.has(key)) {
      return;
    }

    // Skip remote update if there are pending local changes - local state takes priority
    // This prevents our own updates echoed back from the server from overwriting newer local changes
    if (this.pendingLocalChanges.has(key)) {
      return;
    }

    const property = this.properties.get(key);
    if (!property) return;

    // Mark this property as being updated from remote to prevent broadcast echo
    this.remoteUpdateInProgress.add(key);

    try {
      // Update the property value - this triggers subscribeToChanges but scheduleSave
      // will skip broadcasting because remoteUpdateInProgress contains this key
      property.next(value);
    } finally {
      // Clear the flag
      this.remoteUpdateInProgress.delete(key);
    }

    // Cancel the save that was just scheduled by the subscribeToChanges callback
    const newTimer = this.saveTimers.get(key);
    if (newTimer) {
      clearTimeout(newTimer);
      this.saveTimers.delete(key);
    }
  }

  /**
   * Batch update multiple properties at once (saves immediately without debounce)
   * This avoids race conditions when updating related properties together
   *
   * @param propertyValuePairs - Array of [property, value] tuples
   * @example
   * await service.batchUpdateProperties([
   *   [positionProperty, { x: 1, y: 2, z: 3 }],
   *   [normalProperty, { x: 0, y: 0, z: 1 }],
   *   [toleranceProperty, 0.1]
   * ]);
   */
  async batchUpdateProperties(propertyValuePairs: Array<[ReactiveProperty<PropertyValueData>, PropertyValueData]>): Promise<boolean> {
    if (propertyValuePairs.length === 0) {
      return true;
    }

    // Extract update information from property references
    const updates: PropertyUpdate[] = [];
    for (const [property, value] of propertyValuePairs) {
      // Find the property key by searching the map
      let foundKey: string | undefined;
      let foundIdentifier: string | undefined;
      let foundObjectIdentifier: string | undefined;
      let foundPropertyType: PropertyType | undefined;

      for (const [key, prop] of this.properties.entries()) {
        if (prop === property) {
          foundKey = key;
          // Parse key: "identifier:objectIdentifier:propertyType"
          const parts = key.split(':');
          foundIdentifier = parts[0];
          foundObjectIdentifier = parts[1];
          foundPropertyType = parts[2] as PropertyType;
          break;
        }
      }

      if (!foundKey || !foundIdentifier || !foundObjectIdentifier || !foundPropertyType) {
        console.error('Property not found in service:', property);
        continue;
      }

      updates.push({
        identifier: foundIdentifier,
        objectIdentifier: foundObjectIdentifier,
        propertyType: foundPropertyType,
        value,
      });

      // Clear any pending save for this property
      const existingTimer = this.saveTimers.get(foundKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
        this.saveTimers.delete(foundKey);
      }

      // Update the property value (this will trigger subscriptions)
      // Use next() to ensure immediate notification if value changed
      if (property.value !== value) {
        property.next(value);
      }
    }

    if (updates.length === 0) {
      return true;
    }

    // Save all updates in batch using the batch callback
    if (this.config.onSaveBatch) {
      try {
        const success = await this.config.onSaveBatch(updates);
        return success;
      } catch (error) {
        console.error('Error in batch save:', error);
        return false;
      }
    } else {
      // Fallback: save each property individually
      const results = await Promise.all(updates.map((update) => this.saveToDatabase(update.identifier, update.objectIdentifier, update.propertyType, update.value)));
      return results.every((r) => r);
    }
  }

  /**
   * Batch update multiple properties at once (saves immediately without debounce)
   * This avoids race conditions when updating related properties together
   *
   * @deprecated Use batchUpdateProperties with property references instead
   */
  async batchUpdate(updates: PropertyUpdate[]): Promise<boolean> {
    if (updates.length === 0) {
      return true;
    }

    // Suppress individual broadcasts during batch — we'll fire one batch broadcast at the end
    this.batchInProgress = true;

    // Update all property values immediately (triggers subscriptions but not individual saves)
    for (const update of updates) {
      const key = this.createKey(update.identifier, update.objectIdentifier, update.propertyType);

      // Clear any pending save for this property
      const existingTimer = this.saveTimers.get(key);
      if (existingTimer) {
        clearTimeout(existingTimer);
        this.saveTimers.delete(key);
      }

      // Update the property value (this will trigger subscriptions)
      const property = this.getProperty(update.identifier, update.objectIdentifier, update.propertyType);
      // Use next() to ensure immediate notification if value changed
      if (property.value !== update.value) {
        property.next(update.value);
      }
    }

    this.batchInProgress = false;

    // Fire one batch broadcast instead of N individual ones
    if (this.config.onImmediateChangeBatch) {
      this.config.onImmediateChangeBatch(updates);
    } else if (this.config.onImmediateChange) {
      // Fallback: fire individually
      for (const update of updates) {
        this.config.onImmediateChange(update.identifier, update.objectIdentifier, update.propertyType, update.value);
      }
    }

    // Save all updates in batch using the batch callback
    if (this.config.onSaveBatch) {
      try {
        const success = await this.config.onSaveBatch(updates);
        return success;
      } catch (error) {
        console.error('Error in batch save:', error);
        return false;
      }
    } else {
      // Fallback: save each property individually
      const results = await Promise.all(updates.map((update) => this.saveToDatabase(update.identifier, update.objectIdentifier, update.propertyType, update.value)));
      return results.every((r) => r);
    }
  }

  /**
   * Schedule a save to database (debounced)
   * Also triggers immediate callback for real-time updates
   */
  private scheduleSave(identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData): void {
    // In localOnlyMode, skip all network activity (broadcasts + saves)
    // The caller is responsible for persistence (e.g., the GraphQL mutation)
    if (this.localOnlyMode) return;

    const key = this.createKey(identifier, objectIdentifier, propertyType);

    // Skip broadcasting if this change originated from a remote update
    // (prevents echoing updates back to other clients)
    if (!this.remoteUpdateInProgress.has(key)) {
      // Mark as having pending local changes - remote updates will be skipped until save completes
      this.pendingLocalChanges.add(key);
      // Trigger immediate callback (for real-time updates via websockets)
      // Skip during batchUpdate — it sends one batch broadcast at the end instead
      if (!this.batchInProgress) {
        this.config.onImmediateChange?.(identifier, objectIdentifier, propertyType, value);
      }
    }

    // Clear existing timer
    const existingTimer = this.saveTimers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Schedule new save
    const timer = setTimeout(async () => {
      this.saveTimers.delete(key);
      await this.saveToDatabase(identifier, objectIdentifier, propertyType, value);
      // Clear pending local changes flag after save completes
      this.pendingLocalChanges.delete(key);
    }, this.config.debounceMs);

    this.saveTimers.set(key, timer);
  }

  /**
   * Save property to database immediately (bypasses debounce)
   */
  async saveImmediately(identifier: string, objectIdentifier: string, propertyType: PropertyType): Promise<boolean> {
    const key = this.createKey(identifier, objectIdentifier, propertyType);

    // Cancel pending save
    const existingTimer = this.saveTimers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.saveTimers.delete(key);
    }

    const property = this.getProperty(identifier, objectIdentifier, propertyType);
    return await this.saveToDatabase(identifier, objectIdentifier, propertyType, property.value);
  }

  /**
   * Internal method to save to database
   */
  private async saveToDatabase(identifier: string, objectIdentifier: string, propertyType: PropertyType, value: PropertyValueData): Promise<boolean> {
    if (!this.config.onSave) {
      console.warn('PropertyService: onSave callback not configured, property will not be persisted');
      return false;
    }

    try {
      const success = await this.config.onSave(identifier, objectIdentifier, propertyType, value);
      if (!success) {
        console.error(`Failed to save property ${identifier} for ${objectIdentifier}`);
      }
      return success;
    } catch (error) {
      console.error(`Error saving property ${identifier}:`, error);
      return false;
    }
  }

  /**
   * Subscribe to a property's changes
   */
  subscribe(identifier: string, objectIdentifier: string, propertyType: PropertyType, callback: (value: PropertyValueData) => void): Subscription {
    const property = this.getProperty(identifier, objectIdentifier, propertyType);
    return property.subscribe(callback);
  }

  /**
   * Destroy a specific property instance
   */
  destroyProperty(identifier: string, objectIdentifier: string, propertyType: PropertyType): void {
    const key = this.createKey(identifier, objectIdentifier, propertyType);

    // Cancel pending save
    const timer = this.saveTimers.get(key);
    if (timer) {
      clearTimeout(timer);
      this.saveTimers.delete(key);
    }

    // Destroy property
    const property = this.properties.get(key);
    if (property) {
      property.destroy();
      this.properties.delete(key);
    }
  }

  /**
   * Destroy all properties for a specific object
   */
  destroyObjectProperties(objectIdentifier: string): void {
    const keysToDestroy: string[] = [];

    for (const key of this.properties.keys()) {
      if (key.includes(`:${objectIdentifier}:`)) {
        keysToDestroy.push(key);
      }
    }

    for (const key of keysToDestroy) {
      const parts = key.split(':');
      if (parts.length === 3) {
        this.destroyProperty(parts[0], parts[1], parts[2] as PropertyType);
      }
    }
  }

  /**
   * Get all active property instances
   */
  getAllProperties(): Array<{
    identifier: string;
    objectIdentifier: string;
    propertyType: PropertyType;
    value: PropertyValueData;
  }> {
    const result: Array<{
      identifier: string;
      objectIdentifier: string;
      propertyType: PropertyType;
      value: PropertyValueData;
    }> = [];

    for (const [key, property] of this.properties.entries()) {
      const parts = key.split(':');
      if (parts.length === 3) {
        result.push({
          identifier: parts[0],
          objectIdentifier: parts[1],
          propertyType: parts[2] as PropertyType,
          value: property.value,
        });
      }
    }

    return result;
  }

  /**
   * Collect all properties that have pending debounce timers.
   * Cancels the timers and returns the updates that would have been saved.
   */
  getPendingSaves(): PropertyUpdate[] {
    const pending: PropertyUpdate[] = [];

    for (const [key, timer] of this.saveTimers.entries()) {
      clearTimeout(timer);

      const parts = key.split(':');
      if (parts.length === 3) {
        const property = this.properties.get(key);
        if (property) {
          pending.push({
            identifier: parts[0],
            objectIdentifier: parts[1],
            propertyType: parts[2] as PropertyType,
            value: property.value,
          });
        }
      }
    }

    this.saveTimers.clear();
    return pending;
  }

  /**
   * Flush all pending debounced saves immediately via batch save.
   * Returns true if all saves succeeded (or there was nothing to save).
   */
  async flushPendingSaves(): Promise<boolean> {
    const pending = this.getPendingSaves();
    if (pending.length === 0) return true;

    if (this.config.onSaveBatch) {
      try {
        return await this.config.onSaveBatch(pending);
      } catch (error) {
        console.error('Error flushing pending saves:', error);
        return false;
      }
    }

    // Fallback: save each property individually
    const results = await Promise.all(pending.map((u) => this.saveToDatabase(u.identifier, u.objectIdentifier, u.propertyType, u.value)));
    return results.every((r) => r);
  }

  /**
   * Flush pending saves synchronously using the onBeforeUnloadFlush callback.
   * Intended for use in beforeunload handlers where async operations are unreliable.
   */
  flushPendingSavesSync(): void {
    if (!this.config.onBeforeUnloadFlush) return;

    const pending = this.getPendingSaves();
    if (pending.length === 0) return;

    this.config.onBeforeUnloadFlush(pending);
  }

  /**
   * Clear all properties and timers (useful for cleanup or testing)
   */
  destroy(): void {
    // Cancel all pending saves
    for (const timer of this.saveTimers.values()) {
      clearTimeout(timer);
    }
    this.saveTimers.clear();

    // Destroy all properties
    for (const property of this.properties.values()) {
      property.destroy();
    }
    this.properties.clear();
  }

  /**
   * Get statistics about the service
   */
  getStats() {
    return {
      activeProperties: this.properties.size,
      pendingSaves: this.saveTimers.size,
      totalSubscribers: Array.from(this.properties.values()).reduce((sum, prop) => sum + prop.subscriberCount, 0),
    };
  }
}
